// Free weather API using Open-Meteo (no API key required)
// Docs: https://open-meteo.com/en/docs

export interface WeatherData {
  temperature: number;
  weatherCode: number;
  icon: string;
  description: string;
}

// Weather code to icon/description mapping
const weatherCodeMap: Record<number, { icon: string; description: string }> = {
  0: { icon: '☀️', description: 'Clear sky' },
  1: { icon: '🌤️', description: 'Mainly clear' },
  2: { icon: '⛅', description: 'Partly cloudy' },
  3: { icon: '☁️', description: 'Overcast' },
  45: { icon: '🌫️', description: 'Foggy' },
  48: { icon: '🌫️', description: 'Foggy' },
  51: { icon: '🌦️', description: 'Light drizzle' },
  53: { icon: '🌦️', description: 'Drizzle' },
  55: { icon: '🌧️', description: 'Heavy drizzle' },
  61: { icon: '🌧️', description: 'Light rain' },
  63: { icon: '🌧️', description: 'Rain' },
  65: { icon: '⛈️', description: 'Heavy rain' },
  71: { icon: '🌨️', description: 'Light snow' },
  73: { icon: '🌨️', description: 'Snow' },
  75: { icon: '❄️', description: 'Heavy snow' },
  77: { icon: '🌨️', description: 'Snow grains' },
  80: { icon: '🌦️', description: 'Light showers' },
  81: { icon: '🌧️', description: 'Showers' },
  82: { icon: '⛈️', description: 'Heavy showers' },
  85: { icon: '🌨️', description: 'Light snow showers' },
  86: { icon: '❄️', description: 'Snow showers' },
  95: { icon: '⛈️', description: 'Thunderstorm' },
  96: { icon: '⛈️', description: 'Thunderstorm with hail' },
  99: { icon: '⛈️', description: 'Severe thunderstorm' },
};

// City coordinates mapping for Houston metro area
const CITY_COORDS: Record<string, { lat: number; lon: number }> = {
  'Houston': { lat: 29.7604, lon: -95.3698 },
  'Sugar Land': { lat: 29.6197, lon: -95.6349 },
  'Katy': { lat: 29.7858, lon: -95.8244 },
  'Pearland': { lat: 29.5636, lon: -95.2861 },
  'The Woodlands': { lat: 30.1658, lon: -95.4613 },
  'Spring': { lat: 30.0799, lon: -95.4172 },
  'Cypress': { lat: 29.9691, lon: -95.6972 },
  'Humble': { lat: 29.9988, lon: -95.2621 },
  'Missouri City': { lat: 29.6185, lon: -95.5377 },
  'League City': { lat: 29.5074, lon: -95.0949 },
};

export async function getWeatherForDate(
  date: string,
  city: string = 'Houston'
): Promise<WeatherData | null> {
  try {
    // Get coordinates for the city, fallback to Houston if not found
    const coords = CITY_COORDS[city] || CITY_COORDS['Houston'];
    const { lat, lon } = coords;

    const targetDate = new Date(date);
    const dateStr = targetDate.toISOString().split('T')[0]; // YYYY-MM-DD

    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=America/Chicago&temperature_unit=fahrenheit&start_date=${dateStr}&end_date=${dateStr}`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const data = await response.json();
    if (!data.daily || data.daily.time.length === 0) return null;

    const weatherCode = data.daily.weathercode[0];
    const tempMax = data.daily.temperature_2m_max[0];
    const tempMin = data.daily.temperature_2m_min[0];
    const avgTemp = Math.round((tempMax + tempMin) / 2);

    const weatherInfo = weatherCodeMap[weatherCode] || {
      icon: '🌤️',
      description: 'Unknown',
    };

    return {
      temperature: avgTemp,
      weatherCode,
      icon: weatherInfo.icon,
      description: weatherInfo.description,
    };
  } catch (error) {
    console.error('Error fetching weather:', error);
    return null;
  }
}

export function formatTemperature(temp: number): string {
  return `${temp}°F`;
}
